<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Landing Pages List</h1>
        <span class="page-subtitle">Manage landing pages</span>
      </div>
      <div class="page-header__right">
        <button class="btn-primary-action" @click="$router.push('/landing-pages/create')">
          <i class="fa fa-plus"></i> Create a Landing Page
        </button>
      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Type</th>
            <th>Description</th>
            <th>Status</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in items" :key="item.id" class="dd-table__row">
            <td class="text-muted">{{ item.id }}</td>
            <td><span class="item-title">{{ item.title }}</span></td>
            <td>
              <span
                class="type-badge"
                :class="`type-badge--${getTypeValue(item.page_type)}`"
                v-if="getTypeValue(item.page_type)"
              >{{ getTypeLabel(item.page_type) }}</span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td class="text-muted">{{ item.description ? item.description.substr(0, 100) + '...' : '—' }}</td>
            <td v-html="getIsPublishedForHuman(item.is_published)"></td>
            <td @click.stop>
              <b-dropdown variant="none" no-caret right boundary="viewport">
                <template #button-content>
                  <div class="action-menu-btn"><i class="fa fa-caret-down"></i></div>
                </template>
                <b-dropdown-item @click="showTranslator(item.id)">Translate</b-dropdown-item>
                <b-dropdown-item @click="$router.push(`/landing-pages/${item.id}/edit`)">Edit Landing Page</b-dropdown-item>
                <b-dropdown-item @click="deleteLandingPage(item)">Delete</b-dropdown-item>
              </b-dropdown>
            </td>
          </tr>

          <tr v-if="items && items.length === 0">
            <td colspan="6" class="empty-state">
              <i class="fa fa-file-text-o"></i>
              <div>No landing pages found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <translator
        ref="translator"
        :keys="translateKeys"
        model="LandingPage"
        :model_id="modelID"
    >

    </translator>
  </div>
</template>

<script>
import { config } from "../../helpers";
import Translator from "@/components/Translator.vue";

export default {
  name: "LandingPages",
  components: {Translator},
  data() {
    return {
      url: "",
      items: [],
      translateKeys: [
        {
          "key" : "title",
          "type": "text",
        },
        {
          "key" : "title_menu",
          "type": "text",
        },
        {
          "key" : "description",
          "type": "editor",
        },
      ],
      modelID: 0
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/landing-pages";
    this.loadData();
  },

  methods: {
    loadData() {
      axios.get(this.url).then((response) => {
        this.items = response.data.data;
      });
    },

    deleteLandingPage(landingPage) {
      if (confirm(`Do you want to delete ${landingPage.title}?`)) {
        axios.delete(`${this.url}/${landingPage.id}`);
        this.loadData();
      }
    },

    // page_type is an object, e.g. { value: "occasion", label: "Occasion" }
    getTypeValue(pageType) {
      if (!pageType) return "";
      return typeof pageType === "object" ? pageType.value || "" : pageType;
    },

    getTypeLabel(pageType) {
      if (!pageType) return "";
      if (typeof pageType === "object" && pageType.label) return pageType.label;
      const value = this.getTypeValue(pageType);
      return value.charAt(0).toUpperCase() + value.slice(1);
    },

    getIsPublishedForHuman(isPublished) {
      return isPublished
        ? '<span class="status-badge status-badge--green">Published</span>'
        : '<span class="status-badge status-badge--amber">Draft</span>';
    },
    showTranslator(model_id){
      this.modelID = parseInt(model_id);
      this.$refs.translator.showTranslateModal();
    }
  },
};
</script>

<style scoped>
.page-wrap    { display: flex; flex-direction: column; gap: 16px; }
.page-header  { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }
.page-header__right { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

.btn-primary-action { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-primary-action:hover { opacity: .88; }

/* Table */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; white-space: nowrap; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.item-title { font-weight: 600; color: var(--text-1); }
.text-muted { color: var(--text-3); font-size: 12px; }
.type-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 6px; font-size: 11px; text-transform: capitalize; background: rgba(99,179,237,.12); color: #63b3ed; border: 1px solid rgba(99,179,237,.25); white-space: nowrap; }

.type-badge--recipient { background: rgba(129,140,248,.12); color: #818cf8; border-color: rgba(129,140,248,.25); }

.action-menu-btn { width: 30px; height: 30px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 7px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-2); font-size: 14px; }
.action-menu-btn:hover { border-color: var(--accent); color: var(--accent); }
.dd-table td:last-child .btn { padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }

.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

@media (max-width: 768px) {
  .page-header { flex-direction: column; align-items: flex-start; }
}
</style>
